/* eslint-disable sonarjs/no-duplicate-string */

import { addDays, addHours, addMonths, addWeeks, addYears } from 'date-fns';
import { showCommentFileOption } from 'reactApp/appHelpers/featuresHelpers/features/showCommentFileOption';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import {
    type IAutoDeleteOptions,
    type IExpiresOptions,
    AccessStatus,
    AutoDeletePeriod,
    DomainAccess,
    ExpiryPeriod,
} from 'reactApp/components/SharingWindow/Sharing.types';

export const HOT_KEY_SCOPE_NAME = 'SharingWindowDialog';

// TODO: когда уберем фича-флаг, сделать объектом
export const getAccessFileRightsOptions = () => {
    const commonOptions = {
        [AccessStatus.ReadOnly]: {
            id: AccessStatus.ReadOnly,
            text: 'смотреть и скачивать',
            dropdownText: 'Только смотреть и скачивать',
        },
        [AccessStatus.Edit]: {
            id: AccessStatus.Edit,
            text: 'редактировать',
            dropdownText: 'Редактировать документ',
        },
    };

    if (showCommentFileOption) {
        return {
            [AccessStatus.Comment]: {
                id: AccessStatus.Comment,
                text: 'комментировать',
                dropdownText: 'Комментировать',
            },
            ...commonOptions,
        };
    }

    return commonOptions;
};

// TODO: когда уберем фича-флаг, сделать объектом
export const getAccessFileRightsNoDownloadOptions = () => {
    const commonOptions = {
        [AccessStatus.ReadOnly]: {
            id: AccessStatus.ReadOnly,
            text: 'смотреть',
            dropdownText: 'Только смотреть',
        },
        [AccessStatus.Edit]: {
            id: AccessStatus.Edit,
            text: 'редактировать',
            dropdownText: 'Редактировать документ',
        },
    };

    if (showCommentFileOption) {
        return {
            [AccessStatus.Comment]: {
                id: AccessStatus.Comment,
                text: 'комментировать',
                dropdownText: 'Комментировать',
            },
            ...commonOptions,
        };
    }

    return commonOptions;
};

export const ACCESS_FOLDER_RIGHTS_OPTIONS = {
    [AccessStatus.ReadOnly]: {
        id: AccessStatus.ReadOnly,
        text: 'смотреть и скачивать',
        dropdownText: 'Только смотреть и скачивать',
    },
    [AccessStatus.Upload]: {
        id: AccessStatus.Upload,
        text: 'смотреть, скачивать и загружать',
        dropdownText: 'Смотреть, скачивать и загружать',
    },
};

export const ACCESS_FOLDER_RIGHTS_NO_DOWNLOAD_OPTIONS = {
    [AccessStatus.ReadOnly]: {
        id: AccessStatus.ReadOnly,
        text: 'смотреть',
        dropdownText: 'Только смотреть',
    },
    [AccessStatus.Upload]: {
        id: AccessStatus.Upload,
        text: 'смотреть и загружать',
        dropdownText: 'Смотреть и загружать',
    },
};

export const EXPIRES_OPTIONS: Record<string, IExpiresOptions> = {
    [ExpiryPeriod.Unlimited]: {
        id: ExpiryPeriod.Unlimited,
        text: 'Всегда',
        getValue: () => 0,
    },
    [ExpiryPeriod.Hour]: {
        id: ExpiryPeriod.Hour,
        text: 'Час',
        getValue: (): number => addHours(new Date(), 1).valueOf(),
    },
    [ExpiryPeriod.Day]: {
        id: ExpiryPeriod.Day,
        text: 'День',
        getValue: (): number => addDays(new Date(), 1).valueOf(),
    },
    [ExpiryPeriod.Week]: {
        id: ExpiryPeriod.Week,
        text: 'Неделя',
        getValue: (): number => addWeeks(new Date(), 1).valueOf(),
    },
    [ExpiryPeriod.Month]: {
        id: ExpiryPeriod.Month,
        text: 'Месяц',
        getValue: (): number => addMonths(new Date(), 1).valueOf(),
    },
    [ExpiryPeriod.Months3]: {
        id: ExpiryPeriod.Months3,
        text: '3 месяца',
        getValue: (): number => addMonths(new Date(), 3).valueOf(),
    },
    [ExpiryPeriod.HalfYear]: {
        id: ExpiryPeriod.HalfYear,
        text: 'Полгода',
        getValue: (): number => addMonths(new Date(), 6).valueOf(),
    },
    [ExpiryPeriod.Year]: {
        id: ExpiryPeriod.Year,
        text: 'Год',
        getValue: (): number => addYears(new Date(), 1).valueOf(),
    },
};

export const AUTODELETE_OPTIONS: Record<string, IAutoDeleteOptions> = {
    [AutoDeletePeriod.None]: {
        id: AutoDeletePeriod.None,
        text: 'Не удалять',
        getValue: () => 0,
    },
    [AutoDeletePeriod.Day]: {
        id: AutoDeletePeriod.Day,
        text: 'Удалить через день',
        getValue: (): number => addDays(new Date(), 1).valueOf(),
    },
    [AutoDeletePeriod.Week]: {
        id: AutoDeletePeriod.Week,
        text: 'Удалить через неделю',
        getValue: (): number => addWeeks(new Date(), 1).valueOf(),
    },
    [AutoDeletePeriod.Month]: {
        id: AutoDeletePeriod.Month,
        text: 'Удалить через месяц',
        getValue: (): number => addMonths(new Date(), 1).valueOf(),
    },
    [AutoDeletePeriod.Months3]: {
        id: AutoDeletePeriod.Months3,
        text: 'Удалить через 3 месяца',
        getValue: (): number => addMonths(new Date(), 3).valueOf(),
    },
    [AutoDeletePeriod.HalfYear]: {
        id: AutoDeletePeriod.HalfYear,
        text: 'Удалить через полгода',
        getValue: (): number => addMonths(new Date(), 6).valueOf(),
    },
    [AutoDeletePeriod.Year]: {
        id: AutoDeletePeriod.Year,
        text: 'Удалить через год',
        getValue: (): number => addYears(new Date(), 1).valueOf(),
    },
};

export const DOMAIN_ACCESS_OPTIONS = {
    [DomainAccess.EXTERNAL]: {
        id: DomainAccess.EXTERNAL,
        text: 'всем',
        dropdownText: 'Всем',
    },
    [DomainAccess.DOMESTIC]: {
        id: DomainAccess.DOMESTIC,
        text: 'коллегам',
        dropdownText: 'Коллегам',
    },
    [DomainAccess.EMAIL_LIST_ACCESS]: {
        id: DomainAccess.EMAIL_LIST_ACCESS,
        text: 'добавленным пользователям',
        dropdownText: 'Добавленным пользователям',
    },
};

export enum QA_VALUE {
    accessItem = 'item_access',
    accessDropdown = 'dropdown_access',
    accessDropdownContent = 'dropdownContent_access',
    expiresItem = 'item_expires',
    expiresDropdown = 'dropdown_expires',
    expiresDropdownContent = 'dropdownContent_expires',
    domesticItem = 'item_domestic',
    domesticDropdown = 'dropdown_domestic',
    domesticDropdownContent = 'dropdownContent_domestic',
    autoDeleteItem = 'item_autoDelete',
    autoDeleteDropdown = 'dropdown_autoDelete',
    autoDeleteDropdownContent = 'dropdownContent_autoDelete',
    // tempexp_17341-next-line
    changeDesign = 'change_design',
    // tempexp_17605-next-line
    publicPassword = 'public_password',
}

export const AB_FEATURE_NAME = 'new-sharing';
export const AB_FEATURE_NAME_MODIFIED = 'sharing-modified';

export const TOOLTIP_OFFSET = { y: 4 };

export const getTitle = ({
    isPublic,
    isMounted,
    name,
    isEditDisabled = false,
    isAbNewSharingFolder,
    isSharingTabsFeature,
    variant,
}: {
    isPublic: boolean;
    isMounted: boolean;
    name: string;
    isEditDisabled?: boolean;
    isAbNewSharingFolder?: boolean;
    isSharingTabsFeature?: boolean;
    variant?: SHARING_VARIANT;
}): string => {
    // tempexp_15752-next-line
    if (isPublic || isEditDisabled) {
        return 'Совместный доступ';
    }

    if (isMounted) {
        return `Доступ к папке${isSharingTabsFeature ? '' : ` «${name}»`}`;
    }

    if (name && variant === SHARING_VARIANT.foldersBiz) {
        return 'Персональный доступ к папкe';
    }

    // tempexp_15752-start
    if (isAbNewSharingFolder && name) {
        return `Включить совместный доступ для`;
    }
    // tempexp_15752-end

    return 'Настройка совместного доступа';
};
